import React from "react";
import Select from "../../../../components/Select/Select";
import Button from "../../../../components/Button/Button";
import { IconArrowDown, IconClose, IconReset, IconSearch } from "../../../../icons";
import { TagsSelectType } from "../../../../components/Filters/Filters";
import { DocumentTypesEnum } from "../../../../core/enums/PublicationTypesEnum";
import { SORT_ENUM } from "../../../../components/Filters/Filters.helpers";

interface FilterBarProps {
    ribbonOptions: { id: number; title: React.ReactNode }[] | undefined;
    catLoad: boolean;
    tagsOptions: { id: string | number; title: string }[];
    tagLoad: boolean;
    packageLoad: boolean;
    selectedTags: { id: string | number; title: string }[];
    filters: {
        ribbonsIds: number[];
        categoriesIds: number[];
        subscriptionPackageIds: number[];
        tagsIds: number[];
        orderBy: string;
        sortOrder: string;
        hasAccess: boolean;
        types: string[];
        searchQuery?: string;
    };
    onChange: (next: FilterBarProps["filters"]) => void;
    localSearchQuery: string;
    onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSearchClear: () => void;
    hasFilters: boolean;
    mode: "tile" | "table";
    getRem: (px: number) => number;
    isTagsDropdownDisabled?: boolean;
    searchInputRef?: React.RefObject<HTMLInputElement>;
    onSearchBlur?: () => void;
    macRibbonId?: number;
}

const FiltersBar: React.FC<FilterBarProps> = ({
    ribbonOptions,
    catLoad,
    tagsOptions,
    tagLoad,
    packageLoad,
    selectedTags,
    filters,
    onChange,
    localSearchQuery,
    onSearchChange,
    onSearchClear,
    hasFilters,
    mode,
    getRem,
    isTagsDropdownDisabled,
    searchInputRef,
    onSearchBlur,
    macRibbonId
}) => {
    const isOnlyMacSelected = filters.types.length === 1 && filters.types[0] === DocumentTypesEnum.Mac;
    const isMenuAdoptionCycleSelected = filters.ribbonsIds.includes(macRibbonId ?? 0);

    const shouldShowResetButton = (filters: FilterBarProps["filters"], hasFilters: boolean, searchText: string): boolean => {
        const hasPublication = filters.types.includes(DocumentTypesEnum.Publication);
        return hasFilters || !hasPublication || !!searchText.trim();
    };

    return (
        <div className="BrowseSection__filters">
            <div className="BrowseSection__dropdown">
                <Select
                    inputButton
                    buttonContent={(isOpen: boolean) => (
                        <span className="BrowseSection__button-label">
                            {ribbonOptions?.find((r) => r.id === filters.ribbonsIds[0])?.title || "Report Categories"}
                            <IconArrowDown
                                style={{
                                    transform: isOpen ? "rotate(180deg)" : undefined,
                                    transition: "transform 0.2s ease",
                                    fontSize: "12px"
                                }}
                            />
                        </span>
                    )}
                    options={ribbonOptions || []}
                    isLoading={catLoad}
                    value={filters.ribbonsIds}
                    onChange={(ids) =>
                        onChange({
                            ...filters,
                            ribbonsIds: ids as number[],
                            tagsIds: [],
                            subscriptionPackageIds: []
                        })
                    }
                    isSearchable={false}
                    autoFocus
                    dropdownHeight={370}
                    styles={{ selectDropdown: { maxWidth: getRem(250) } }}
                    preventResetOnSelected
                    isCheckboxHidden
                    showTickIcon
                    selectedOnTop
                    useBrowseStyles
                />
            </div>

            <div className="BrowseSection__dropdown">
                <Select
                    inputButton
                    isDisabled={isTagsDropdownDisabled || isOnlyMacSelected || isMenuAdoptionCycleSelected}
                    buttonContent={(isOpen: boolean) => (
                        <span className="BrowseSection__button-label">
                            Tags
                            <IconArrowDown
                                style={{
                                    transform: isOpen ? "rotate(180deg)" : undefined,
                                    transition: "transform 0.2s ease",
                                    fontSize: "12px"
                                }}
                            />
                        </span>
                    )}
                    options={tagsOptions}
                    isLoading={tagLoad || packageLoad}
                    value={selectedTags.map((t) => t.id)}
                    onChange={(ids) => {
                        const newTagIds: number[] = [];
                        const newPackageIds: number[] = [];
                        ids.forEach((id) => {
                            const [parsedType, parsedIdStr] = (id as string).split("-");
                            const parsedId = +parsedIdStr;
                            if (!parsedId) return;
                            if (parsedType === TagsSelectType.Tag) newTagIds.push(parsedId);
                            else if (parsedType === TagsSelectType.Package) newPackageIds.push(parsedId);
                        });
                        onChange({
                            ...filters,
                            tagsIds: newTagIds,
                            subscriptionPackageIds: newPackageIds
                        });
                    }}
                    isSearchable
                    searchPlaceholder="Search"
                    autoFocus={mode !== "table"}
                    dropdownHeight={350}
                    styles={{ selectDropdown: { minWidth: getRem(270) } }}
                    isMultiple
                    isCheckboxHidden={false}
                    closeOnSelect={false}
                    hasApplyCancel
                    preventResetOnSelected
                    useBrowseStyles
                />

                {mode === "table" && (
                    <div className="BrowseSection__search">
                        <div className="BrowseSection__search-field">
                            <div className="BrowseSection__search-icon">
                                <IconSearch />
                            </div>
                            <input
                                ref={searchInputRef}
                                className="BrowseSection__search-input"
                                type="text"
                                placeholder="Search Reports"
                                value={localSearchQuery}
                                onChange={onSearchChange}
                                onBlur={onSearchBlur}
                            />
                            {localSearchQuery && (
                                <Button className="BrowseSection__search-clear" onClick={onSearchClear} options={["tertiary"]}>
                                    <IconClose />
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                {shouldShowResetButton(filters, hasFilters, localSearchQuery) && (
                    <div className="Filters__column is-right">
                        <div className="Filters__reset">
                            <Button
                                className="Filters__reset-button"
                                onClick={() => {
                                    onSearchClear();
                                    onChange({
                                        ...filters,
                                        ribbonsIds: [],
                                        tagsIds: [],
                                        subscriptionPackageIds: [],
                                        sortOrder: SORT_ENUM.DESC,
                                        orderBy: SORT_ENUM.DATE,
                                        searchQuery: "",
                                        types: [DocumentTypesEnum.Publication]
                                    });
                                }}
                                options={["tertiary"]}
                                unstyled
                            >
                                <div className="Filters__reset-icon">
                                    <IconReset />
                                </div>
                                Reset all
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FiltersBar;
